import { SubmissionError, getFormValues, reset} from 'redux-form';
import _forEach from 'lodash/forEach';
import _get from 'lodash/get';
import _find from 'lodash/find';
import http from '../../../shared/http';
import qs from 'qs';

import endpoints from '../../../../config/endpoints';
import { selectors as parentSelectors } from '../../profile';
import { getDateRangeFromCalendar, getUtcDateRange } from '../../../shared/calendar';

import {createSelectOptionsFromSysEnums} from '../../../shared/form-elements/select';

const defaultInitialState = {
	fields: {
		offers: null,
		target_approve: null
	},
	offerApproveData: null,
	isShow: false,
	isBlockedSubmitButton: false
};

const prepareFormErrors = errors => {
	const validationsErrors = {};
	_forEach(errors, value => {
		const key = value.dataPath.slice(1);
		validationsErrors[key] = value.message;
	});
	return validationsErrors;
};

const createForm = ({ namespace, stateNodeName, formName }) => {
	const FORM_SUBMIT_SUCCESS = `${namespace}/${stateNodeName}-submit-increase-approve-form-success`;
	const FORM_SUBMIT_ERROR = `${namespace}/${stateNodeName}-submit-increase-approve-form-error`;
	const TOGGLE_FORM = `${namespace}/${stateNodeName}-toggle-increase-approve-form`;
	const OFFERS_LOOKUP_SUCCESS = `${namespace}/${stateNodeName}_OFFERS_LOOKUP_SUCCESS`;
	const FETCH_OFFERS_APPROVE_DATA_SUCCESS = `${namespace}/${stateNodeName}_FETCH_OFFERS_APPROVE_DATA_SUCCESS`;
	const FETCH_OFFERS_APPROVE_DATA_ERROR = `${namespace}/${stateNodeName}_FETCH_OFFERS_APPROVE_DATA_ERROR`;
	const BLOCK_SUBMIT_DATA = `${namespace}/${stateNodeName}_BLOCK_SUBMIT_DATA`;
	const MAKE_DEFAULT_METADATA = `${namespace}/${stateNodeName}_MAKE_DEFAULT_METADATA`;
	const FETCH_LOCATION_LOOKUPS_SUCCESS = `${namespace}/${stateNodeName}_FETCH_LOCATION_LOOKUPS_SUCCESS`;
	const FETCH_LOCATION_LOOKUPS_ERROR = `${namespace}/${stateNodeName}_FETCH_LOCATION_LOOKUPS_ERROR`;

	const initialState = { ...defaultInitialState };

	const actions = {
		requestGarantFormSubmit: () => (dispatch, getState) => {
			const formData = selectors.getFormData(getState());
			const idsToApprove = selectors.getIdsToApprove(getState());
			const date_range = getUtcDateRange(formData.date_range);

			const submitData = {
				...formData,
				country_id: formData.countries[0],
				campaign_id: formData.campaign_ids ? formData.campaign_ids[0] : null,
				offer_id: formData.offers[0],
				idsToApprove,
				target_percent: formData.target_approve,
				starts_at: date_range.start,
				ends_at: date_range.end
			};

			return http({
				method: 'put',
				url: endpoints.CONVERSIONS.REQUEST_GARANT,
				data: submitData,
				actions: [ FORM_SUBMIT_SUCCESS, FORM_SUBMIT_ERROR ]
			})(dispatch)
				.then(action => {
					if (action.type === FORM_SUBMIT_ERROR && action.payload.response.status === 400) {
						const responseErrors = action.payload.response.data;
						throw new SubmissionError(prepareFormErrors(responseErrors));
					} else {
						dispatch(reset('affiliateProfileRequestGarantForm'));
						dispatch({ type: MAKE_DEFAULT_METADATA });
						dispatch(actions.toggleRequestGarantForm());
					}
				});
		},
		approveGarantLocationsLookups: (isCountryCodeAsId, text) => (dispatch, getState) => {
			const { offers } = selectors.getFormData(getState());
			const request = http({
				url: `${endpoints.LOOKUPS.LOCATIONS}?query=${text}&${qs.stringify({ offers })}`,
				method: 'get',
				actions: [FETCH_LOCATION_LOOKUPS_SUCCESS, FETCH_LOCATION_LOOKUPS_ERROR]
			});

			return request(dispatch).then(action => {
				if (action.type === FETCH_LOCATION_LOOKUPS_SUCCESS) {
					return {
						options: createSelectOptionsFromSysEnums(
							action.payload.data.map(item => ({
								...item, id: item[isCountryCodeAsId ? 'country_code' : 'id']
							}))
						)
					};
				}
			});
		},
		fetchOffersGarantApproveData: () => (dispatch, getState) => {
			dispatch(actions.blockSubmitButton());
			const formData = selectors.getFormData(getState());
			const offers = selectors.getFormOffers(getState());

			if (
				!_get(formData, 'offers.length')
				|| !formData.target_approve
				|| !_get(formData, 'countries.length')
			) {
				dispatch({ type: MAKE_DEFAULT_METADATA });
				return ;
			}

			return http({
				method: 'get',
				url: `${endpoints.CONVERSIONS.STATS_FOR_GARANT_APPROVE}?${qs.stringify(formData)}`,
				actions: [ FETCH_OFFERS_APPROVE_DATA_SUCCESS, FETCH_OFFERS_APPROVE_DATA_ERROR ],
				addToResponse: { offers }
			})(dispatch)
				.then(action => {
					if (action.type === FETCH_OFFERS_APPROVE_DATA_ERROR && action.payload.response.status === 400) {
						const responseErrors = action.payload.response.data.errors;
						throw new SubmissionError(prepareFormErrors(responseErrors));
					}
				});
		},
		blockSubmitButton: () => dispatch => {
			dispatch({ type: BLOCK_SUBMIT_DATA  });
		},
		toggleRequestGarantForm: () => dispatch => {
			dispatch({ type: TOGGLE_FORM  });
		},
		campaignsGarantLookup: text => (dispatch, getState) => {
			const { affiliate_id, offers } = selectors.getFormData(getState());
			const offer_id = offers && offers[0];

			if (!offer_id) {
				return Promise.resolve({ options: [] });
			}
			const query = qs.stringify({ query: text, affiliate_id, offer_id: offers && offers[0] });

			const request = http({
				url: `${endpoints.LOOKUPS.CAMPAIGNS}?${query}`,
				method: 'get',
				actions: [OFFERS_LOOKUP_SUCCESS]
			});

			return request(dispatch).then(action => {
				if (action.type === OFFERS_LOOKUP_SUCCESS) {
					return { options: action.payload.data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		},
		offersGarantLookup: text => (dispatch, getState) => {
			const { affiliate_id } = selectors.getFormData(getState());
			const query = qs.stringify({ query: text, affiliate_id, onlyAvailable: true, isShowedRemoved: true });

			dispatch(reset('affiliateProfileRequestGarantForm'));

			const request = http({
				url: `${endpoints.LOOKUPS.OFFERS}?${query}`,
				method: 'get',
				actions: [OFFERS_LOOKUP_SUCCESS]
			});

			return request(dispatch).then(action => {
				if (action.type === OFFERS_LOOKUP_SUCCESS) {
					return { options: action.payload.data.map(item => ({ value: item.id, label: item.text })) };
				}
			});
		}
	};

	const reducers = {
		[TOGGLE_FORM]: (state, action) => ({
			...state, requestGarantForm: {
				...state.requestGarantForm, isShow: !state.requestGarantForm.isShow, isBlockedSubmitButton: false
			}
		}),
		[MAKE_DEFAULT_METADATA]: (state, action) => ({
			...state, requestGarantForm: {
				...state.requestGarantForm, offerApproveData: defaultInitialState.offerApproveData
			}
		}),
		[FORM_SUBMIT_ERROR]: (state, action) => ({
			...state,  requestGarantForm: {
				...state.requestGarantForm, isBlockedSubmitButton: false
			}
		}),
		[FETCH_OFFERS_APPROVE_DATA_ERROR]: (state, action) => ({
			...state,  requestGarantForm: {
				...state.requestGarantForm, isBlockedSubmitButton: false
			}
		}),
		[BLOCK_SUBMIT_DATA]: (state, action) => ({
			...state, requestGarantForm: {
				...state.requestGarantForm, isBlockedSubmitButton: true
			}
		}),
		[FETCH_OFFERS_APPROVE_DATA_SUCCESS]: (state, action) =>  ({
			...state,
			requestGarantForm: {
				...state.requestGarantForm, isBlockedSubmitButton: false,
				offerApproveData: processOffersApproveData(action.payload.data, action.meta.offers).data,
				idsToApprove: processOffersApproveData(action.payload.data, action.meta.offers).idsToApprove
			}
		})
	};

	const selectors = {
		getFormData: state => {
			const formValues = getFormValues('affiliateProfileRequestGarantForm')(state);

			if (!formValues) return { affiliate_id: state.affiliateProfile.affiliateId };

			return {
				...formValues,
				offers: formValues.offers && formValues.offers.map(i => i.value),
				countries: formValues.countries && formValues.countries.map(i => i.value),
				campaign_ids: formValues.campaign_ids && formValues.campaign_ids.map(i => i.value),
				date_range: getDateRangeFromCalendar(parentSelectors.getGarantRequestCalendar(state)),
				affiliate_id: state.affiliateProfile.affiliateId
			};
		},
		getFormOffers: state => {
			const formValues = getFormValues('affiliateProfileRequestGarantForm')(state);
			if (!formValues || !formValues.offers) return [];

			return formValues.offers;
		},
		getIdsToApprove: state => _get( state, 'affiliateProfile.requestGarantForm.idsToApprove'),
		getRequestGarantFormVisibility: state =>  state.affiliateProfile.requestGarantForm.isShow,
		getIsBlockedSubmitButton: state =>  state.affiliateProfile.requestGarantForm.isBlockedSubmitButton,
		getAvailableCurrencies: state => state.root.lists['available-currencies'],
		getRequestGarantFormOfferApproveData: state =>  state.affiliateProfile.requestGarantForm.offerApproveData
	};

	return {
		initialState,
		actions,
		reducers,
		selectors
	};
};

function processOffersApproveData(data, offers) {
	const idsToApprove = { trash: [], rejected: [], pending: [] };

	return {
		data: offers && offers.map(offer => {
			const toBePayed = { };
			const item = _find(data, { offer_id: offer.value });
			if (!item) {
				return { has_leads: false, total_count: 0, leads_per_day: 0, offer_title: offer.label, toBePayed };
			}
			item.rejectedToApproveData && item.rejectedToApproveData.forEach(rtad => {
				toBePayed[rtad.currency] = toBePayed[rtad.currency] ? toBePayed[rtad.currency] + rtad.payout : rtad.payout;
				idsToApprove['rejected'].push(rtad.id);
			});
			item.trashToApproveData && item.trashToApproveData.forEach(rtad => {
				toBePayed[rtad.currency] = toBePayed[rtad.currency] ? toBePayed[rtad.currency] + rtad.payout : rtad.payout;
				idsToApprove['trash'].push(rtad.id);
			});
			item.pendingToApproveData && item.pendingToApproveData.forEach(rtad => {
				toBePayed[rtad.currency] = toBePayed[rtad.currency] ? toBePayed[rtad.currency] + rtad.payout : rtad.payout;
				idsToApprove['pending'].push(rtad.id);
			});

			return { ...item, toBePayed, has_leads: true };
		}),
		idsToApprove
	};
}

export default createForm;



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/affiliates/components/request-garant-form/index.js